import React, { FC, useEffect } from 'react'
import { Field, Form, Formik, FormikHelpers, FormikProps } from 'formik'
import { t } from '@lingui/macro'
import { loginFormValidationSchema } from 'components/session/Login/LoginForm/LoginFormValidationSchema'
import { useDispatch } from 'react-redux'
import authenticationActions from 'redux-store/authentication/authentication.actions'
import useAuthentication from 'hooks/Authentication'
import { FormValidationError } from 'components/Forms/FormValidationError/FormValidationError'
import { Button } from 'pages/native/onBoarding/components'
import { FormInput } from 'pages/native/onBoarding/components/FormInput/FormInput'

import styles from './LoginForm.module.scss'

interface LoginFormValues {
  userEmail: string
  password: string
}
interface LoginFormProps {
  onForgotPasswordClick: () => void
}
export const LoginForm: FC<LoginFormProps> = ({ onForgotPasswordClick }) => {
  const { loggingIn, error } = useAuthentication()
  const dispatch = useDispatch()

  const initialValues: LoginFormValues = {
    userEmail: '',
    password: ''
  }

  const onSubmit = async (
    values: LoginFormValues,
    formikHelpers: FormikHelpers<LoginFormValues>
  ) => {
    formikHelpers.setSubmitting(true)
    dispatch(
      authenticationActions.login(
        values.userEmail.toLowerCase(),
        values.password
      )
    )
    formikHelpers.setSubmitting(false)
  }

  useEffect(() => {
    dispatch(authenticationActions.clearState())
  }, [])

  return (
    <div className={styles.LoginFormContainer}>
      <Formik
        initialValues={initialValues}
        validationSchema={loginFormValidationSchema}
        onSubmit={onSubmit}
        validateOnChange
        validateOnBlur
      >
        {(formik: FormikProps<LoginFormValues>) => {
          return (
            <Form className={styles.Form}>
              <Field
                component={FormInput}
                id="userEmail"
                name="userEmail"
                label={t`Email`}
                placeholder={t`Eg: john@email.com`}
                type="email"
                isRequired
                value={formik.values.userEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  formik.handleChange(e)
                  formik.validateField('userEmail')
                }}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.trim()
                  formik.handleBlur(e)
                }}
                isValid={!(formik.touched.userEmail && formik.errors.userEmail)}
                validationMessage={formik.errors.userEmail}
              />

              <Field
                component={FormInput}
                id="password"
                name="password"
                label={t`Password`}
                placeholder={t`Enter your password`}
                type="password"
                value={formik.values.password}
                isRequired
                showTogglePassword
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  formik.handleChange(e)
                  formik.validateField('password')
                }}
                onBlur={formik.handleBlur}
                isValid={!(formik.touched.password && formik.errors.password)}
                validationMessage={formik.errors.password}
              />
              {error?.message && (
                <FormValidationError errorMessage={error.message} />
              )}
              <Button
                variant="text"
                theme="support"
                textAlign="left"
                type="button"
                onClick={() => onForgotPasswordClick()}
                label={
                  <div className={styles.TextInline}>
                    <span
                      className={styles.ClickableText}
                    >{t`Forgot your password?`}</span>
                  </div>
                }
              />
              <Button
                label={t`Login`}
                variant="filled"
                theme="primary"
                type="submit"
                disabled={
                  !formik.isValid ||
                  !formik.dirty ||
                  formik.isSubmitting ||
                  loggingIn
                }
                className={styles.LoginButton}
                data-testid="login-form-button"
              />
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}
